import { useSearchParams } from 'react-router-dom'
import { applyRiskSelectionPatch, parseRiskSelection } from '../utils/riskUrlParams'
import type { RiskSelection, RiskSelectionPatch } from '../types/riskSelection.types'

export type ElementGroupsMode = 'replace' | 'union'
export type AnalysisMode = 'by-group' | 'by-type'

const isAnalysisMode = (value: string): value is AnalysisMode =>
  value === 'by-group' || value === 'by-type'

export function useRiskFilters() {
  const [searchParams, setSearchParams] = useSearchParams()

  const selection: RiskSelection = parseRiskSelection(searchParams)

  const setFilters = (
    patch: RiskSelectionPatch,
    options?: { elementGroupsMode?: ElementGroupsMode },
  ) => {
    const resolvedPatch: RiskSelectionPatch =
      patch.elementGroups !== undefined && options?.elementGroupsMode === 'union'
        ? {
            ...patch,
            elementGroups: Array.from(
              new Set([...selection.elementGroups, ...patch.elementGroups]),
            ),
          }
        : patch

    setSearchParams(applyRiskSelectionPatch(searchParams, resolvedPatch), { replace: true })
  }

  const setAnalysisMode = (nextMode: string) => {
    if (!isAnalysisMode(nextMode)) return
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams)
        nextParams.set('analysis', nextMode)
        return nextParams
      },
      { replace: true },
    )
  }

  // Focuses the analysis on a single element layer and switches to the type view.
  // Use when the user selects exactly one element from element dropdown selection shows the element detail summary component
  // instead of the multi-element chart.
  const filterToSingleElement = (elementId: string) => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams)
        nextParams.set('elementGroups', elementId)
        nextParams.set('analysis', 'by-type')
        return nextParams
      },
      { replace: true },
    )
  }

  return { ...selection, setFilters, setAnalysisMode, filterToSingleElement }
}
